import { AGM_SAVE_ERRORS, AGM_VIEWS } from '@/constants/agm';
import * as agmApi from '@/utils/agm-api';

// The AGM RSVP: the one response from GET /v2/agm (§4.2 of
// docs/plans/2026-10-22-agm-rsvp-mobile-migration.md), the member's answer, and what
// the page is doing about it. Both pages and the Me page's row read it, so opening
// the briefing from /agm costs no second request.
//
// The response decides the state: `view` is derived here, and the components hold no
// rules. State is the GET body plus `loading`, `submitting` and the two error flags.
//
// This is member data (whether they are an EM, whether they attend), so it is wiped
// on sign-out (mixins/Logout.js, `agm/reset`), is never cached beyond this session,
// and nothing here logs a response body or an axios error: a failure is reported by
// its status code alone.

const emptyBody = () => ({
  eligible: null,
  agmActive: false,
  event: null,
  rsvp: null,
  rsvpOpen: false,
  rsvpClosesAt: null,
  links: null,
  briefing: null,
});

const initialState = () => ({
  ...emptyBody(),
  // A GET has succeeded this session. Until then there is nothing to show but a skeleton.
  loaded: false,
  loading: false,
  // The last GET failed: the page shows F, with Try again.
  loadFailed: false,
  // The answer being saved (true = Yes, false = No), or null. The tapped button shows
  // the spinner and the other is disabled, so a double tap cannot send both answers.
  submitting: null,
  // Why the last save did not land (AGM_SAVE_ERRORS), shown inside the RSVP card.
  saveError: null,
});

const state = initialState;

// A response is only trusted if it says whether the member is eligible: anything else
// (an empty body, an HTML error page behind a proxy) is a failed load, not a screen.
const isAgmBody = body =>
  Boolean(body) && typeof body === 'object' && typeof body.eligible === 'boolean';

const hasPassed = iso => {
  const time = iso ? new Date(iso).getTime() : NaN;
  return !Number.isNaN(time) && Date.now() >= time;
};

// The calls in flight when the member signs out must not write the next member's
// store: `reset` moves the generation on, and a response from an older one is dropped.
let generation = 0;
let loadInFlight = null;

const getters = {
  view: state => {
    if (!state.loaded)
      return state.loadFailed && !state.loading ? AGM_VIEWS.error : AGM_VIEWS.loading;
    // A refresh that failed is not hidden behind the last answer: after the cutoff that
    // answer may no longer be one the member can still change.
    if (state.loadFailed && !state.loading) return AGM_VIEWS.error;
    if (state.eligible !== true) return AGM_VIEWS.ineligible;
    if (!state.event) return AGM_VIEWS.none;
    if (hasPassed(state.event.endsAt)) return AGM_VIEWS.ended;
    if (!state.rsvpOpen) return AGM_VIEWS.closed;
    return state.rsvp?.attending === true || state.rsvp?.attending === false
      ? AGM_VIEWS.answered
      : AGM_VIEWS.open;
  },

  // true, false, or null for no answer yet.
  answer: state => (typeof state.rsvp?.attending === 'boolean' ? state.rsvp.attending : null),

  // Whether an AGM is current, for the Me page's row. The API's own answer, which is the
  // same for every member and so says nothing about eligibility (D7), and also false once
  // the event's `endsAt` has passed. (Evaluated when the state changes; `prime` reads
  // again when the app has been open across the end.) Never read `eligible` for this.
  agmActive: state => state.agmActive === true && !hasPassed(state.event?.endsAt),
};

const mutations = {
  applyBody(state, body) {
    Object.assign(state, emptyBody(), {
      eligible: body.eligible,
      agmActive: body.agmActive === true,
      event: body.event ?? null,
      rsvp: body.rsvp ?? null,
      rsvpOpen: body.rsvpOpen === true,
      rsvpClosesAt: body.rsvpClosesAt ?? null,
      links: body.links ?? null,
      briefing: body.briefing ?? null,
      loaded: true,
      loadFailed: false,
    });
  },
  setLoading(state, loading) {
    state.loading = loading;
  },
  setLoadFailed(state, failed) {
    state.loadFailed = failed;
  },
  setSubmitting(state, answer) {
    state.submitting = answer;
  },
  setSaveError(state, error) {
    state.saveError = error;
  },
  reset(state) {
    generation += 1;
    loadInFlight = null;
    Object.assign(state, initialState());
  },
};

const actions = {
  /**
   * GET /v2/agm. Every visit to /agm and every pull to refresh calls it: ops can close
   * RSVP at any time, so nothing is cached. A second call while one is in flight
   * shares it. Resolves true when the screen was replaced. Never rejects.
   *
   * `keepSaveError` is for the refetch after a 409, which must not clear the very
   * message it was made to show.
   */
  load({ commit, rootState }, { keepSaveError = false } = {}) {
    if (loadInFlight) return loadInFlight;
    const mine = generation;
    commit('setLoading', true);
    if (!keepSaveError) commit('setSaveError', null);

    const run = (async () => {
      try {
        const body = await agmApi.fetchAgm(rootState.user.token);
        if (mine !== generation) return false;
        if (!isAgmBody(body)) throw new Error('unexpected response');
        commit('applyBody', body);
        return true;
      } catch (error) {
        if (mine === generation) {
          commit('setLoadFailed', true);
          console.warn('[AGM] Could not load, status:', agmApi.agmErrorStatus(error));
        }
        return false;
      } finally {
        if (mine === generation) {
          commit('setLoading', false);
          loadInFlight = null;
        }
      }
    })();
    loadInFlight = run;
    return run;
  },

  // The Me page's row needs `agmActive` before the member ever opens /agm. One quiet read
  // per session: not repeated after a failure, since the page loads for itself when it is
  // opened. The exception is an answer that has gone stale, an AGM the API called current
  // whose `endsAt` has since passed (the app was left open): read again, so the row is
  // gone for the right reason rather than hidden by a cached flag.
  prime({ state, dispatch }) {
    if (state.loading) return false;
    if (state.loaded) {
      if (!(state.agmActive && hasPassed(state.event?.endsAt))) return false;
    } else if (state.loadFailed) {
      return false;
    }
    dispatch('load');
    return true;
  },

  /**
   * PUT /v2/agm/rsvp, then replace the screen from the response. Resolves with:
   * - 'saved': the card is now B, from the response.
   * - 'closed': a 409. RSVP closed while the member was answering; the screen was
   *   refetched, so the page is on C (or F), with `saveError` 'closed' to say why.
   * - 'failed': nothing was saved; `saveError` is 'failed' and the buttons work again.
   * - 'busy': another save is running.
   */
  async setRsvp({ commit, dispatch, state, getters, rootState }, attending) {
    if (state.submitting !== null) return 'busy';
    const mine = generation;
    commit('setSubmitting', attending);
    commit('setSaveError', null);
    try {
      const result = await agmApi.putRsvp(rootState.user.token, attending);
      if (mine !== generation) return 'failed';

      if (result?.closed) {
        commit('setSaveError', AGM_SAVE_ERRORS.closed);
        await dispatch('load', { keepSaveError: true });
        // Still open after a refetch: the 409 was not the cutoff, and a "closed"
        // message on an open card would be wrong. Nothing was saved.
        if (mine === generation && [AGM_VIEWS.open, AGM_VIEWS.answered].includes(getters.view)) {
          commit('setSaveError', AGM_SAVE_ERRORS.failed);
          return 'failed';
        }
        return 'closed';
      }

      if (!isAgmBody(result)) throw new Error('unexpected response');
      commit('applyBody', result);
      return 'saved';
    } catch (error) {
      if (mine === generation) {
        commit('setSaveError', AGM_SAVE_ERRORS.failed);
        console.warn('[AGM] Could not save, status:', agmApi.agmErrorStatus(error));
      }
      return 'failed';
    } finally {
      if (mine === generation) commit('setSubmitting', null);
    }
  },

  clearSaveError({ commit }) {
    commit('setSaveError', null);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
